
import * as Types from '@aeppic/types'
import { parse as queryParse, stringify as queryStringify } from 'query-string'

import Aeppic from './aeppic'
import UrlPattern from 'url-pattern'

import { runActions } from '../dynamic/run-action.js'
import { buildLogger, Logger, InMemoryLog } from '../model/log'
import * as Errors from './errors'
import { BrowserHistoryNavigation } from './browser-history-navigation'
import { closeAllDialogs } from './dialog-stack.js'

export interface EvaluationOptions {
  retainLog?: boolean
}

const DEFAULT_EVALUATION_OPTIONS: EvaluationOptions = {

}

export interface RouteParams {
  [key: string]: string|number|boolean
}

export interface RouteMatchResult {
  redirect?: string
  layout?: Types.IdOrReference
  params?: object
  url: string
  hash: ReturnType<typeof queryParse>
  query: ReturnType<typeof queryParse>
  log?: any
}

export interface NavigationOptions {
  replace?: boolean
}

export interface IRouter {
  onNavigateTo(targetDocumentId: string, parameters?: RouteParams, options?: NavigationOptions): void
  onLinkClicked(event, href): void
  navigateToUrl(url: string): void
  updateRouteParams?(params: RouteParams, options?: { replace?: boolean }): void
  registerSubpathParam?(name: string): void
  reserveBrowserBackEntry?(): void

  evaluate(url: string): Promise<RouteMatchResult>
}

export const DOCUMENT_ID_PLACEHOLDER = '7d4e4903-5ef8-43cd-8399-bb56318e8bd9'
export const ROUTE_QUERY_PARAMETERS_PLACEHOLDER = 'e2282f0b-f933-4b56-9366-fa60face30de'

export function ParameterizedDocumentRoute(strings: TemplateStringsArray, ...params: (string|Symbol)[]) {
  return function buildNavigateToUrl(documentId: string, routeParams: RouteParams) {
    const queryParams = queryStringify(routeParams)
    const urlParts = []

    for (let i = 0; i < strings.length; i++) {
      const fixedString = strings[i]
      urlParts.push(fixedString)

      const dynamicParameter = params[i]

      switch (dynamicParameter) {
        case DOCUMENT_ID_PLACEHOLDER:
          urlParts.push(documentId)
          break
        case ROUTE_QUERY_PARAMETERS_PLACEHOLDER:
          if (queryParams) {
            urlParts.push('?')
            urlParts.push(queryParams)
          }
          break
        default:
          urlParts.push(dynamicParameter)
      }
    }

    return urlParts.join('')
  }
}

export interface RouterOptions {
  Aeppic?: Aeppic
  Logger?: Logger

  defaultLayoutId?: null
  navigateToDocumentUrlBuilder?: ReturnType<typeof ParameterizedDocumentRoute>
}

const DefaultRouterOptions: Partial<RouterOptions> = {
  navigateToDocumentUrlBuilder: ParameterizedDocumentRoute`/docs/${DOCUMENT_ID_PLACEHOLDER}${ROUTE_QUERY_PARAMETERS_PLACEHOLDER}`,
}

interface PrefixedRoutingTable {
  name: string
  prefix: string
  routes: Types.Document[]
}

export interface Route {
  templateUrl: string
  document: Types.Document
  dynamicAction?: Types.Document
}

export class DefaultRouteListBuilder {

  constructor(private _aeppic: Aeppic) {}

  async *enumerate(): AsyncIterableIterator<Route> {
    for await (const table of this.iterateRoutingTables()) {
      const routes = await this._toRoutes(table)
      yield *routes
    }
  }

  async *_toRoutes(table: PrefixedRoutingTable): AsyncIterableIterator<Route> {
    for (const routeDocument of table.routes) {
      const routeUrl = ensurePathPrefix(table.prefix) + ensurePathPrefix(<string> routeDocument.data.route)

      const route: Route = {
        templateUrl: routeUrl,
        document: routeDocument,
      }

      if (<boolean> routeDocument.data.isDynamic) {
        route.dynamicAction = await this._aeppic.get(<Types.Reference> routeDocument.data.action)
      }

      yield route
    }
  }

  async *iterateRoutingTables(): AsyncIterableIterator<PrefixedRoutingTable> {
    const root: any = await this._aeppic.get('root')

    if ('defaultApp' in root.data) {
      const prefixedRoutingTables = this._loadRoutingTablesFromApplication(root.data.defaultApp)
      yield *prefixedRoutingTables
    }

    if ('routingTables' in root.data) {
      const routingTables = this._loadRoutingTables(root.data.routingTables)
      yield *routingTables
    }
  }

  async *_loadRoutingTablesFromApplication(applicationReference: Types.Reference): AsyncIterableIterator<PrefixedRoutingTable> {
    const app = await this._aeppic.get(applicationReference)

    if ('routingTables' in app.data) {
      const routePrefix = <string>app.data.routingPrefixAlias || (<string>app.data.name).toLocaleLowerCase()
      yield * this._loadRoutingTables(<Types.Reference[]> app.data.routingTables, routePrefix)
    }
  }

  async *_loadRoutingTables(routingTables: Types.Reference[], prefix?: string): AsyncIterableIterator<PrefixedRoutingTable> {
    for (const tableReference of routingTables) {
      const loadedTable = await this._loadRoutingTable(tableReference, prefix)
      yield loadedTable
    }
  }

  async _loadRoutingTable(routingTableIdentifier: Types.IdOrReference, prefix: string = ''): Promise<PrefixedRoutingTable> {
    const table = await this._aeppic.get(routingTableIdentifier)

    if (!table) {
      return
    }

    const allRoutePrefix = ensurePathPrefix(prefix) + ensurePathPrefix(<string>table.data.sharedPrefix)
    const routes = await this._aeppic.getAll(<Types.Reference[]>table.data.routes)

    return {
      name: <string> table.data.name,
      prefix: allRoutePrefix,
      routes: routes
    }
  }
}

function ensurePathPrefix(prefixString: string) {
  if (!prefixString) {
    return ''
  }

  if (prefixString.startsWith('/')) {
    return prefixString
  } else {
    return '/' + prefixString
  }
}

export class Router implements IRouter {
  private _window: Window
  private _aeppic: Aeppic
  private _defaultLayoutId: string
  private _navigateToDocumentUrlBuilder: ReturnType<typeof ParameterizedDocumentRoute>
  private _log: Logger
  private _routes: Route[]
  private _parsedRoutes: Map<Route, UrlPattern>
  private _lastNavigatedUrl: string
  private _subpathParams: Set<string>
  private _browserHistory: BrowserHistoryNavigation

  constructor(specifiedOptions: RouterOptions = {}) {
    const options = { ...DefaultRouterOptions, ...specifiedOptions }

    this._log = buildLogger(options.Logger, { module: 'routing', class: 'Router' })
    this._aeppic = options.Aeppic
    this._defaultLayoutId = options.defaultLayoutId
    this._navigateToDocumentUrlBuilder = options.navigateToDocumentUrlBuilder
    this._subpathParams = new Set(['aspect'])
  }

  registerSubpathParam(name: string) {
    this._subpathParams.add(name)
  }

  load() {
    this._log.warn('`load` is deprecated. Use `refresh` instead.')
    return this.refresh()
  }

  async refresh() {
    const routeBuilder = new DefaultRouteListBuilder(this._aeppic)

    this._routes = []

    for await (const route of routeBuilder.enumerate()) {
      this._routes.push(route)
    }

    this._parseRoutes()
  }

  _parseRoutes() {
    this._parsedRoutes = new Map()

    for (const route of this._routes) {
      this._parsedRoutes.set(route, new UrlPattern(route.templateUrl))
    }
  }

  set Routes(routes: Route[]) {
    this._routes = routes
    this._parseRoutes()
  }

  get Routes() {
    return this._routes
  }

  linkToBrowserWindow(windowToLink: Window = window) {
    this._window = windowToLink
    this._log.info('Linking to window (href=%s)', this._window.location.href)

    this._browserHistory = new BrowserHistoryNavigation(windowToLink,
      detail => this._aeppic.dispatchDOMEvent('ae-browser-history-navigation', detail),
      () => this._aeppic.confirmNavigation?.() || null)

    windowToLink.onpopstate = (e) => {
      this._log.trace({ state: e.state }, 'onpopstate')

      if (this._browserHistory.intercept(e.state)) {
        return
      }

      if (e.state) {
        const newHref = e.state.href
        const currentHref = this._lastNavigatedUrl || ''

        const newBase = new URL(this._stripSubpathParams(newHref), this._window.location.href).pathname
        const currentBase = new URL(this._stripSubpathParams(currentHref), this._window.location.href).pathname

        if (newBase === currentBase) {
          const queryParams = queryParse(new URL(newHref, this._window.location.href).search)
          const subpathParams = this._extractSubpathParams(newHref)
          const newParams = {
            ...queryParams,
            ...subpathParams,
          }
          this._lastNavigatedUrl = newHref
          this._aeppic.dispatchDOMEvent('ae-route-params-changed', {
            ...newParams,
            routeParams: { query: queryParams, subpath: subpathParams },
          })
          e.preventDefault()
          return
        }

        this._navigateToUrl(newHref, { omitPushState: true })
        e.preventDefault()
      }
    }

    windowToLink.onhashchange = () => {
      this._log.trace({ href: this._window.location.href }, 'onhashchange')
      if (this._browserHistory.restoring) {
        return
      }
      if (this._lastNavigatedUrl && new URL(this._lastNavigatedUrl, this._window.location.href).href === this._window.location.href) {
        return
      }
      this._navigateToUrl(this._window.location.href)
    }

    this._writeHistory(this._window.location.href, true)
    this._writeHistory(this._window.location.href)
  }

  private _writeHistory(href: string, replace = false) {
    this._browserHistory.write({ href }, href, replace)
  }

  reserveBrowserBackEntry() {
    this._browserHistory.reserveBackEntry()
  }

  onNavigateTo(targetDocumentId: string, parameters?: RouteParams, options?: NavigationOptions): void {
    const { forceNavigate, ...params } = parameters || {} as RouteParams

    const subpathParams: RouteParams = {}
    const queryParams: RouteParams = {}

    for (const [key, value] of Object.entries(params)) {
      if (this._subpathParams.has(key)) {
        subpathParams[key] = value
      } else {
        queryParams[key] = value
      }
    }

    const baseUrl = this._navigateToDocumentUrlBuilder(targetDocumentId, queryParams)
    const documentUrl = this._insertSubpathParams(baseUrl, subpathParams)

    if (!forceNavigate) {
      const currentHref = this._lastNavigatedUrl || ''
      const currentBase = new URL(this._stripSubpathParams(currentHref), this._window.location.href).pathname
      const newBase = this._stripSubpathParams(documentUrl).split(/[?#]/)[0]

      if (currentBase === newBase) {
        this.updateRouteParams(params, { replace: options?.replace })
        return
      }
    }

    this._navigateToUrl(documentUrl, { replace: options?.replace })
  }

  onLinkClicked(event: any, href: string): void {
    this.navigateToUrl(href)
  }

  navigateToUrl(url: string): void {
    this._navigateToUrl(url)
  }

  updateRouteParams(params: RouteParams, options?: { replace?: boolean }) {
    const currentUrl = this._window?.location?.pathname + (this._window?.location?.search || '') + (this._window?.location?.hash || '')
    const baseUrl = this._stripSubpathParams(currentUrl)

    const existingQueryParams = queryParse(this._window.location.search || '') as RouteParams
    const existingSubpathParams = this._extractSubpathParams(currentUrl)
    const mergedParams = { ...existingQueryParams, ...existingSubpathParams, ...params }

    const subpathParams: RouteParams = {}
    const queryParams: RouteParams = {}
    for (const [key, value] of Object.entries(mergedParams)) {
      if (value == null || value === false || value === '') {
        continue
      }

      if (this._subpathParams.has(key)) {
        subpathParams[key] = value
      } else {
        queryParams[key] = value
      }
    }

    const [pathPart, hashPart] = baseUrl.split('#', 2)
    const routePath = pathPart.split('?', 1)[0]
    const queryString = queryStringify(queryParams)
    const urlWithSubpathParams = this._insertSubpathParams(routePath, subpathParams)
    const newUrl = `${urlWithSubpathParams}${queryString ? `?${queryString}` : ''}${hashPart ? `#${hashPart}` : ''}`

    this._lastNavigatedUrl = newUrl
    this._writeHistory(newUrl, options?.replace)
    this._aeppic.dispatchDOMEvent('ae-route-params-changed', {
      ...queryParams,
      ...subpathParams,
      routeParams: { query: queryParams, subpath: subpathParams },
    })
  }

  private _extractSubpathParams(url: string): RouteParams {
    const params: RouteParams = {}
    const [pathPart] = url.split(/[?#]/)

    for (const name of this._subpathParams) {
      const regex = new RegExp(`/${name}/([^/?#]+)`)
      const match = pathPart.match(regex)
      if (match) {
        params[name] = decodeURIComponent(match[1])
      }
    }

    return params
  }

  private _stripSubpathParams(url: string): string {
    let result = url
    for (const name of this._subpathParams) {
      result = result.replace(new RegExp(`/${name}/[^/?#]+`), '')
    }
    return result
  }

  private _insertSubpathParams(url: string, subpathParams: RouteParams): string {
    const segments = []
    for (const [name, value] of Object.entries(subpathParams)) {
      if (value != null && value !== false && value !== '') {
        segments.push(`/${name}/${encodeURIComponent(String(value))}`)
      }
    }

    if (segments.length === 0) {
      return url
    }

    const queryIndex = url.indexOf('?')
    const hashIndex = url.indexOf('#')
    const splitIndex = queryIndex >= 0 ? queryIndex : (hashIndex >= 0 ? hashIndex : -1)

    if (splitIndex >= 0) {
      return url.slice(0, splitIndex) + segments.join('') + url.slice(splitIndex)
    }

    return url + segments.join('')
  }

  private async _navigateToUrl(url: string, { omitPushState, replace }: { omitPushState?: boolean, replace?: boolean } = {}) {
    if (!omitPushState) {
      const confirmation = this._aeppic.confirmNavigation?.()
      if (confirmation && !await confirmation) return
    }

    closeAllDialogs()
    this._log.info('Navigate to url %s %s', url, omitPushState ? '(Not pushing state to history)' : '')

    this._lastNavigatedUrl = url

    if (!omitPushState) {
      this._writeHistory(url, replace)
    }

    try {
      const result = await this.evaluate(url)

      this._aeppic.renderLayout(result.layout, {
        params: {
          ...result.params,
          route: {
            query: result.query,
            hash: result.hash,
            url,
          }
        }
      })
    } catch (error) {
      this._aeppic.renderLayout(this._defaultLayoutId, {
        params: {
          route: {
            url,
            error,
          }
        }
      })
    }
  }

  match(url: string): { match: Route, parameters: RouteParams } {
    if (!this._routes) {
      this._log.info('No routes configured on router')
      return null
    }

    for (const route of this._routes) {
      const parsedRoute = this._parsedRoutes.get(route)

      if (parsedRoute) {
        const matchedParameters = parsedRoute.match(url)

        if (matchedParameters) {
          return { match: route, parameters: matchedParameters }
        }
      }
    }
  }

  async evaluate(navigationUrl: string, specifiedOptions: EvaluationOptions = {}): Promise<RouteMatchResult> {
    const url = ensureRelativeUrl(navigationUrl)

    const options = { ...DEFAULT_EVALUATION_OPTIONS, ...specifiedOptions }

    let   log = this._log.child({ url })

    const [baseUrl, queryString, hash] = splitUrl(url)

    const subpathParams = this._extractSubpathParams(baseUrl)
    const baseUrlWithoutSubpaths = this._stripSubpathParams(baseUrl)

    const queryParameters = queryParse(queryString)
    const hashParameters = queryParse(hash)

    const route = this.match(baseUrlWithoutSubpaths)

    const routeParams = { ...(route && route.parameters) || {}, ...subpathParams }

    const defaultResult: RouteMatchResult = {
      layout: this._defaultLayoutId,
      url,
      query: queryParameters,
      hash: hashParameters,
      params: routeParams
    }

    if (!route) {
      log.info({ url }, 'No matching route found for url: %s', url)
      return defaultResult
    } else {
      log = log.child({ route: await this._aeppic.asReference(route.match.document), parameters: route.parameters, query: queryParameters, hash: hashParameters })

      log.trace({ matchedRoute: route }, 'Matched `%s` to route "%s" (%s@%s)', url, route.match.document.data.name, route.match.document.id, route.match.document.v)

      const dynamicRouteAction = route.match.dynamicAction

      if (!dynamicRouteAction) {
        const assignedLayout = await this._aeppic.get(<Types.Reference> route.match.document.data.layout)

        if (!assignedLayout) {
          log.warn({ route }, 'Matched `%s` to route "%s" (%s@%s) with NO layout configured or layout not resolvable', url, route.match.document.data.name, route.match.document.id, route.match.document.v)
          throw new Errors.InvalidRouteConfiguration(url)
        }

        return {
          ...defaultResult,
          layout: await this._aeppic.asReference(assignedLayout)
        }
      } else {
        const context = {
          route: {
            url,
            document: route.match.document,
            params: route.parameters,
            query: queryParameters,
            hash: hashParameters,
          },
        }

        log.trace({ context }, 'Executing action %s@%s', dynamicRouteAction.id, dynamicRouteAction.v)

        const MAX_ROUTER_LOG_SIZE = 1000

        let actionLogger: Logger
        let inMemoryLog: InMemoryLog

        if (options.retainLog) {
          inMemoryLog = new InMemoryLog({ size: MAX_ROUTER_LOG_SIZE, defaults: { scope: 'routing', source: 'action', action: await this._aeppic.asReference(dynamicRouteAction) }})
          actionLogger = inMemoryLog.child()
        } else {
          actionLogger = log.child({ source: 'action', action: await this._aeppic.asReference(dynamicRouteAction) })
        }


        const actionResults = await runActions([ dynamicRouteAction ], { context }, { Logger: actionLogger })

        const primaryResult = actionResults[0]

        if (!primaryResult.ok) {
          log.error({ result: primaryResult, context, action: await this._aeppic.asReference(dynamicRouteAction) }, 'Error \'%s\' executing Action %s@%s', primaryResult.error, dynamicRouteAction.id, dynamicRouteAction.v)
          throw new Errors.DynamicRouteProcessing(url)
        }

        if (primaryResult.writes && primaryResult.writes.length > 0) {
          log.error({ result: primaryResult, context, actionRef: this._aeppic.asReference(dynamicRouteAction) }, 'Error executing Action %s@%s. It tried to write to Aeppic', dynamicRouteAction.id, dynamicRouteAction.v)
          throw new Errors.DynamicRouteWrite(url)
        }

        const logMessages = options.retainLog ? Array.from(inMemoryLog.enumerate()) : undefined

        log.trace({ result: primaryResult, context, action: await this._aeppic.asReference(dynamicRouteAction), log: logMessages }, 'Executed Action %s@%s', dynamicRouteAction.id, dynamicRouteAction.v)

        const routeResult: RouteMatchResult = {
          ...defaultResult,
          layout: primaryResult.value.layout,
          params: primaryResult.value.params,
          redirect: primaryResult.value.redirect,
          log: logMessages,
        }

        log.debug({ routeResult }, 'Evaluated route %s@%s dynamically with action %s@%s', route.match.document.id, route.match.document.v, dynamicRouteAction.id, dynamicRouteAction.v )

        return routeResult
      }
    }
  }
}

function splitUrl(url: string) {
  const indexOfQuery = url.indexOf('?')

  if (indexOfQuery < 0) {
    const indexOfHash = url.indexOf('#')

    if (indexOfHash < 0) {
      return [url, '', '']
    }

    const baseUrl = url.slice(0, indexOfHash)
    const suffix = url.slice(indexOfHash + 1)

    return [baseUrl, '', suffix]
  } else {
    const baseUrl = url.slice(0, indexOfQuery)
    const suffix = url.slice(indexOfQuery + 1)

    const indexOfHash = suffix.indexOf('#')

    if (indexOfHash < 0) {
      return [baseUrl, suffix, '']
    }

    return [baseUrl, suffix.slice(0, indexOfHash), suffix.slice(indexOfHash + 1)]
  }
}

function ensureRelativeUrl(url: string) {
  if (url.toLowerCase().startsWith('http')) {
    const parsedUrl = new URL(url)
    return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash
  } else {
    return url
  }
}
